import { Facebook, IconComponent, Instagram, X } from '@vgw/multibrand-fe-icon'
import { Link } from '@vgw/multibrand-fe-link'
import { FooterNavigation } from './footer-navigation'
import { CookieConsentBanner } from '@/features/cookie-consent-banner/cookie-consent-banner'
import { Trans, useLingui } from '@lingui/react/macro'
import { Disclaimer } from './disclaimer'
import { LicenseDetails } from './license-details'
import { FOOTER_LOGOS } from './config'
import { useAmoeLaunch } from '@/features/amoe/use-amoe-launch'
import {
  getBrandConfig,
  SocialMediaLinks,
  SocialMediaPlatform,
} from '@/config/config'
import { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { useCookieConsentBannerStore } from '@/features/cookie-consent-banner/cookie-consent-banner-store'
import { cn } from '@vgw/tailwind-merger'

type FooterNavIcon = {
  icon: IconComponent
  url: string
  title: MessageDescriptor
  className?: string
}

type FooterNavProps = {
  rsgIcons: readonly FooterNavIcon[]
  socialMediaIcons: readonly FooterNavIcon[]
}

const SOCIAL_MEDIA_CONFIG: Record<
  SocialMediaPlatform,
  { icon: IconComponent; title: MessageDescriptor }
> = {
  facebook: { icon: Facebook, title: msg`Go to Facebook` },
  instagram: { icon: Instagram, title: msg`Go to Instagram` },
  x: { icon: X, title: msg`Go to X` },
}

const createSocialMediaIcons = (
  socialMediaLinks: SocialMediaLinks,
): FooterNavIcon[] => [
  {
    icon: SOCIAL_MEDIA_CONFIG.facebook.icon,
    url: socialMediaLinks.facebook,
    title: SOCIAL_MEDIA_CONFIG.facebook.title,
    className: 'size-5',
  },
  {
    icon: SOCIAL_MEDIA_CONFIG.instagram.icon,
    url: socialMediaLinks.instagram,
    title: SOCIAL_MEDIA_CONFIG.instagram.title,
    className: 'size-5',
  },
  {
    icon: SOCIAL_MEDIA_CONFIG.x.icon,
    url: socialMediaLinks.x,
    title: SOCIAL_MEDIA_CONFIG.x.title,
    className: 'size-5',
  },
]

export type FooterProps = {
  suppressAmoe?: boolean
}
export const Footer = (props: FooterProps) => {
  const { open: openAMOEDialog } = useAmoeLaunch()
  const brandConfig = getBrandConfig()
  const socialMediaIcons = createSocialMediaIcons(brandConfig.socialMediaLinks)
  const isCookiesBannerVisible = useCookieConsentBannerStore(
    (state) => state.isCookiesBannerVisible,
  )

  return (
    <footer className="relative self-stretch bg-surface px-2 py-10 pb-20 sm:px-4 lg:px-6 lg:pb-10">
      <div className="mx-auto flex w-full max-w-(--breakpoint-max-2xl) flex-col gap-6 text-xs text-content">
        <FooterNavigation
          suppressAmoe={!!props.suppressAmoe}
          onAMOEDialogOpen={openAMOEDialog}
        />
        <NavIcons rsgIcons={FOOTER_LOGOS} socialMediaIcons={socialMediaIcons} />
        <Disclaimer />
        <LicenseDetails />
      </div>
      {isCookiesBannerVisible ? <CookieConsentBanner /> : null}
    </footer>
  )
}

const NavIcons = ({ rsgIcons, socialMediaIcons }: FooterNavProps) => {
  const { pamLegalDocumentsUrl } = getBrandConfig()

  return (
    <div className="flex flex-col items-center justify-center gap-4 md:flex-row md:gap-0 lg:m-auto lg:gap-16">
      <div
        className="flex flex-wrap items-center justify-center gap-4 md:flex-nowrap md:gap-2.5 lg:justify-between lg:gap-4"
        data-testid="footer-logos"
      >
        {createIconsGroup(rsgIcons)}
        <Link
          key="rsg-link"
          size="xs"
          target="_blank"
          rel="noopener"
          href={`${pamLegalDocumentsUrl}/responsible-social-gameplay-policy`}
          className="px-1.5 py-1"
          weight="medium"
        >
          <Trans>
            Gameplay can be harmful if not kept in control.
            <br />
            Please play responsibly.
          </Trans>
        </Link>
      </div>
      <div className="flex gap-4 md:gap-2.5">
        {createIconsGroup(socialMediaIcons)}
      </div>
    </div>
  )
}

const createIconsGroup = (icons: readonly FooterNavIcon[]) => {
  return icons.map(({ icon: Icon, url, title, className }, i) => {
    const { t } = useLingui()
    return (
      <Link
        key={i}
        href={url}
        target="_blank"
        rel="noopener"
        title={t(title)}
        className="hover:opacity-50"
      >
        <Icon className={cn('text-content', className)} aria-hidden />
      </Link>
    )
  })
}
